import 'dayjs/locale/es';

import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowExpandIcon } from '../../assets';
import { CONTROL_INFINITE_SCROLLING_PAGE_SIZE } from '../../config/constants';
import { LIST_MORE_PIECES_GROUPED } from '../../graphql/pieces/query';
import { useInfiniteScrollObserver } from '../../hooks/useIfiniteScrollObserver';
import type {
  AccessDataListMorePiecesGrouped,
  ControlPiecesFilters,
  GroupedPiece,
  SinglePiece,
} from '../../types';
import { StatusText } from '../common/StatusText';
import { InfinitePieceList } from './InfinitePieceList';

dayjs.extend(localizedFormat);

interface GroupedListelementProps {
  data: GroupedPiece;
  loadMore: () => void;
  hasMore: boolean;
  handleClick: (campaignId: string, influencerId: string) => void;
  grouped_id: string;
  name: string;
  type: 'status' | 'month' | 'account_manager' | 'brand';
  filters: ControlPiecesFilters;
  updateGroupedData: (newData: SinglePiece[]) => void;
}

export const GroupedListelement = ({
  data,
  handleClick,
  grouped_id,
  name,
  type,
  filters,
  updateGroupedData,
}: GroupedListelementProps) => {
  const { t, i18n } = useTranslation('common');
  dayjs.locale(i18n.language);
  const [opened, setOpened] = useState(true);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [fetch, { loading: loadingQuery }] =
    useLazyQuery<AccessDataListMorePiecesGrouped>(LIST_MORE_PIECES_GROUPED, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (res: AccessDataListMorePiecesGrouped) => {
        if (res.listMorePiecesGrouped.data) {
          updateGroupedData(res.listMorePiecesGrouped.data);
        }
      },
    });

  const loadMore = () => {
    if (!loadingQuery && data.count > data.pieces.length) {
      fetch({
        variables: {
          ...filters,
          grouped_id,
          offset: (pageIndex + 1) * CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
          limit: CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
        },
      });
      setPageIndex(pageIndex + 1);
    }
  };

  const ref = useInfiniteScrollObserver(loadMore);

  return (
    <div className="flex flex-col gap-y-2">
      <div
        onClick={() => setOpened(!opened)}
        className="w-full flex flex-row justify-between cursor-pointer bg-dark-blue p-2.5 rounded-[4px]"
      >
        <div className="flex flex-row gap-x-2">
          <div className={`${opened && 'rotate-90'}`}>
            <ArrowExpandIcon />
          </div>
          <span>
            {type === 'status' && (
              <StatusText status={name as any} type="action" />
            )}
            {type === 'month' && (
              <span className="capitalize">
                {dayjs(name, 'MM/YYYY').format('MMMM YYYY')}
              </span>
            )}
            {type === 'account_manager' && name}
            {type === 'brand' && name}
          </span>
        </div>
        <div>
          <span>
            {t('controlView.total')}: {data.count}
          </span>
        </div>
      </div>
      <div className="px-2">
        {opened && data.pieces && (
          <InfinitePieceList
            data={data.pieces}
            handleClick={handleClick}
            hasMore={data.pieces.length < data.count}
            loadMore={loadMore}
            hasPublicationDate={filters.has_publication_date}
          />
        )}
      </div>
      <div ref={ref} style={{ height: 1 }} />
    </div>
  );
};
